import AttributionManager from './components/AttributionManager';
import type { AttributionCookie } from './components/AttributionManager';
import MainHeader from './components/MainHeader';
import attachOpenCookieSettings from './components/OpenCookieSettings';

/**
 * Controller class.
 *
 * Standardizes how we hook into event listeners and create components.
 */
class Controller {
	constructor() {
		window.dataLayer = window.dataLayer || [];
		this.init();

		window.addEventListener('resize', this.resized);
		window.addEventListener('load', this.loaded);
	}

	init() {
		MainHeader.init();

		window.AttributionManager = new AttributionManager();

		// Init and update external links when attribution changes.
		this.appendAttributionToExternalLinks();
		window.addEventListener('AttributionManager:update', () => {
			this.appendAttributionToExternalLinks();
		});

		this.smoothScrollToAnchors();

		// TODO: Room for improvement in Quiq init.
		this.initQuiq();
		this.addChatBtnDataLayer();
		attachOpenCookieSettings();
	}

	resized() {
		MainHeader.resized();
	}

	loaded() {
		MainHeader.loaded();
	}

	addChatBtnDataLayer() {
		const observer = new MutationObserver(() => {
			const chatBtn = document.querySelector<HTMLElement>('.quiq-view-openchat');
			if (chatBtn) {
				// Add click event once the btn is visible.
				chatBtn.addEventListener('click', () => {
					window.dataLayer.push({
						event: 'chat_interaction',
						interactionType: 'click',
						interactionLocation: 'floating_button',
					});
				});

				// Stop observing once the button is found and event is attached.
				observer.disconnect();
			}
		});

		observer.observe(document.body, { childList: true, subtree: true });
	}

	// Currently configured to append UTMs to all external links, not just booking.
	// Could create CMS Environment Setting field to list domains to append UTM's on.
	appendAttributionToExternalLinks() {
		if (!window.AttributionManager) return;

		const attributionParams: AttributionCookie = window.AttributionManager.getData();

		if (!attributionParams) return;

		const currentDomain = window.location.hostname;
		const links = document.querySelectorAll<HTMLAnchorElement>('a');

		links.forEach((link) => {
			const href = link.getAttribute('href');
			if (
				!href ||
				href.startsWith('#') ||
				href.startsWith('mailto:') ||
				href.startsWith('tel:')
			) {
				return;
			}

			const url = new URL(href, window.location.origin);

			if (url.hostname !== currentDomain) {
				const newQueryParams = new URLSearchParams(url.search);
				Object.entries(attributionParams).forEach(([key, value]) => {
					const keyLower = key.toLowerCase();
					if (!newQueryParams.has(keyLower) && typeof value === 'string') {
						newQueryParams.append(keyLower, value);
					}
				});

				url.search = newQueryParams.toString();
				link.href = url.toString();
			}
		});
	}

	smoothScrollToAnchors() {
		const links = document.querySelectorAll<HTMLAnchorElement>(
			'a[href^="#"], a[href^="."], a[href^="["]'
		);

		links.forEach((link) => {
			link.addEventListener('click', (event) => {
				const targetSelector = link.getAttribute('href');
				if (!targetSelector) return;

				// Check if the selector is valid.
				try {
					document.querySelector(targetSelector);
				} catch (e) {
					// Invalid selector.
					return true;
				}

				const targetElement = document.querySelector(targetSelector);
				if (!targetElement) return;

				event.preventDefault();

				const headerOffset =
					parseInt(
						getComputedStyle(document.documentElement).getPropertyValue(
							'--headerHeightFull'
						),
						10
					) || 0;

				const targetPosition = targetElement.getBoundingClientRect().top + window.scrollY;

				window.scrollTo({
					top: targetPosition - headerOffset,
					behavior: 'smooth',
				});
			});
		});
	}

	waitForQuiq(interval = 100, maxAttempts = 100) {
		return new Promise((resolve, reject) => {
			let attempts = 0;
			const check = setInterval(() => {
				if (window.Quiq) {
					clearInterval(check);
					resolve(window.Quiq);
				}
				if (++attempts > maxAttempts) {
					clearInterval(check);

					const chatTriggers = document.querySelectorAll<HTMLElement>('[href="#chat"]');

					if (chatTriggers) {
						chatTriggers.forEach((chatTrigger) => {
							const parentMenuItem = chatTrigger.closest('.menu-item');
							if (parentMenuItem) {
								parentMenuItem.remove();
							} else {
								chatTrigger.remove();
							}
						});
					}

					reject(new Error('Quiq did not load.'));
				}
			}, interval);
		});
	}

	initQuiq() {
		this.waitForQuiq().then(() => {
			if (!window.Quiq) return;
			const chat = window.Quiq({
				pageConfigurationId: 'client-support',
			});

			const chatTriggers = document.querySelectorAll<HTMLElement>('[href="#chat"]');

			chatTriggers.forEach((chatTrigger) => {
				chatTrigger.addEventListener('click', (event: Event) => {
					event.preventDefault();

					window.dataLayer.push({
						event: 'chat_interaction',
						interactionType: 'click',
						interactionLocation: 'inline',
					});

					chat.show();
				});
			});
		});
	}
}

// Run controller.
new Controller();
